import { useEffect } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';

/**
 * Resource Priority Optimizer
 * Manages resource loading priorities for optimal mobile performance
 */
export const ResourcePriorityOptimizer = () => {
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!isMobile) return;

    // Preload critical fonts
    const preloadFont = (href: string) => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'font';
      link.type = 'font/woff2';
      link.crossOrigin = 'anonymous';
      link.href = href;
      document.head.appendChild(link);
    };

    // Defer non-critical scripts
    const deferScripts = () => {
      const scripts = document.querySelectorAll('script[data-defer="true"]');
      scripts.forEach((script) => {
        script.setAttribute('defer', 'true');
      });
    };

    // Optimize image loading priority
    const optimizeImagePriority = () => {
      // Set fetchpriority="high" for LCP image
      const lcpImages = document.querySelectorAll('img[data-lcp="true"]');
      lcpImages.forEach((img) => {
        img.setAttribute('fetchpriority', 'high');
        img.setAttribute('loading', 'eager');
      });

      // Set fetchpriority="low" for below-fold images
      const belowFoldImages = document.querySelectorAll('img:not([data-lcp="true"])');
      belowFoldImages.forEach((img) => {
        if (!img.hasAttribute('loading')) {
          img.setAttribute('loading', 'lazy');
          img.setAttribute('fetchpriority', 'low');
        }
      });
    };

    // Run optimizations
    if (window.requestIdleCallback) {
      window.requestIdleCallback(() => {
        deferScripts();
        optimizeImagePriority();
      });
    } else {
      setTimeout(() => {
        deferScripts();
        optimizeImagePriority();
      }, 1);
    }
  }, [isMobile]);

  return null;
};
